import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CatalogCreatorCard, CatalogResponse } from "../../domain";
import { apiClient } from "../../data";
import { mockCatalog } from "../../data/mockCatalog";
import { analytics } from "../../analytics";
import "./CatalogPage.css";

/* ── helpers ─────────────────────────────────────────────────────────────────── */

function fmtFollowers(n?: number | null): string {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".0", "") + " M";
  if (n >= 100_000)   return Math.round(n / 1000) + " K";
  if (n >= 10_000)    return (n / 1000).toFixed(1).replace(".0", "") + " K";
  return n.toLocaleString("ru-RU");
}

function fmtPlays(n?: number | null): string {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".0", "") + " M";
  if (n >= 1_000)     return Math.round(n / 1000) + " K";
  return String(n);
}

function fmtDate(d?: string | null): string | null {
  if (!d) return null;
  // already DD.MM.YYYY
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(d)) return d;
  // ISO: YYYY-MM-DD or YYYY-MM-DDTHH:...
  const iso = d.slice(0, 10).split("-");
  if (iso.length === 3) return `${iso[2]}.${iso[1]}.${iso[0]}`;
  return d;
}

function getInitials(name?: string | null, username?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (username || "??").slice(0, 2).toUpperCase();
}

/* ── icons ───────────────────────────────────────────────────────────────────── */

const VerifIcon = () => (
  <svg className="cc-verif" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l2.4 1.8 3 .1 1 2.8 2.4 1.7-1 2.8 1 2.8-2.4 1.7-1 2.8-3 .1L12 22l-2.4-1.8-3-.1-1-2.8L3.2 15l1-2.8-1-2.8 2.4-1.7 1-2.8 3-.1z" />
    <path d="M8.5 12l2.2 2.2 4.8-4.8" fill="none" stroke="var(--bg)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FollowersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </svg>
);

/* ── SkeletonCard ────────────────────────────────────────────────────────────── */

const SkeletonCard: React.FC = () => (
  <div className="skeleton-card">
    <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
      <div className="skel skel-circle" style={{ width: 54, height: 54, flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, paddingTop: 5 }}>
        <div className="skel skel-h" style={{ width: "72%" }} />
        <div className="skel skel-h sm" style={{ width: "50%" }} />
        <div className="skel skel-h sm" style={{ width: "38%" }} />
      </div>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
      <div className="skel skel-block" style={{ height: 60 }} />
      <div className="skel skel-block" style={{ height: 60 }} />
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <div className="skel skel-h" style={{ width: "100%" }} />
      <div className="skel skel-h" style={{ width: "90%" }} />
      <div className="skel skel-h sm" style={{ width: "65%" }} />
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 13, borderTop: "1px solid var(--border)" }}>
      <div className="skel skel-h sm" style={{ width: 90 }} />
      <div className="skel skel-h sm" style={{ width: 100 }} />
    </div>
  </div>
);

/* ── CreatorCard ─────────────────────────────────────────────────────────────── */

const CreatorCard: React.FC<{ card: CatalogCreatorCard; onClick: () => void }> = ({ card, onClick }) => {
  const date = fmtDate(card.last_report_date);
  const initials = getInitials(card.name, card.username);

  return (
    <article
      className="creator-card"
      onClick={() => { analytics.catalogCardClicked(card.username); onClick(); }}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onClick()}
    >
      {/* head */}
      <div className="cc-head">
        <div className="cc-av">
          {card.profile_picture_url
            ? <img src={card.profile_picture_url} alt={`@${card.username}`} />
            : initials}
        </div>
        <div className="cc-meta">
          <div className="cc-username">
            @{card.username}
            <VerifIcon />
          </div>
          {card.name && <div className="cc-name">{card.name}</div>}
          {card.followers_count != null && (
            <div className="cc-followers">
              <FollowersIcon />
              {fmtFollowers(card.followers_count)}&nbsp;подписчиков
            </div>
          )}
        </div>
      </div>

      {/* stats chips */}
      <div className="cc-stats">
        {card.avg_er != null && (
          <div className="cc-stat">
            <div className="cc-stat-val">
              {card.avg_er.toFixed(2)}<span className="unit">%</span>
            </div>
            <div className="cc-stat-lbl">Вовлечённость</div>
          </div>
        )}
        {card.avg_plays_per_reel != null && (
          <div className="cc-stat">
            <div className="cc-stat-val">
              {fmtPlays(Math.round(card.avg_plays_per_reel))}
            </div>
            <div className="cc-stat-lbl">Просмотров / Reel</div>
          </div>
        )}
      </div>

      {/* message / spacer */}
      {card.catalog_message
        ? <p className="cc-message">«{card.catalog_message}»</p>
        : <div className="cc-spacer" />
      }

      {/* footer */}
      <div className="cc-footer">
        {date ? <span className="cc-date">Отчёт от {date}</span> : <span />}
        <button className="cc-cta">
          Смотреть отчёт <ArrowRightIcon />
        </button>
      </div>
    </article>
  );
};

/* ── CatalogPage ─────────────────────────────────────────────────────────────── */

export const CatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);

  const [data, setData] = React.useState<CatalogResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setError(null);

    const stale = process.env.REACT_APP_USE_MOCK_CATALOG === "true"
      ? mockCatalog
      : apiClient.getCatalogCached(page);

    if (stale) {
      setData(stale);
      setLoading(false);
    } else {
      setLoading(true);
    }

    (async () => {
      try {
        const result = process.env.REACT_APP_USE_MOCK_CATALOG === "true"
          ? mockCatalog
          : await apiClient.getCatalog(page);
        if (!cancelled) {
          setData(result);
          setLoading(false);
          analytics.catalogViewed(page);
        }
      } catch {
        if (!cancelled && !stale) setError("Не удалось загрузить каталог");
      }
    })();
    return () => { cancelled = true; };
  }, [page]);

  const goToPage = (p: number) => {
    setSearchParams({ page: String(p) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="catalog-page">

      {/* ── HERO ── */}
      <div className="catalog-hero">
        <div className="catalog-eyebrow">Рекламодателям</div>
        <h1 className="catalog-title">Каталог блогеров</h1>
      </div>

      {/* ── CONTENT ── */}
      <main className="catalog-section">

        {/* loading */}
        {loading && (
          <div className="catalog-loading">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* error */}
        {!loading && error && (
          <div className="catalog-grid">
            <div className="catalog-state">
              <div className="state-icon red">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v5" strokeLinecap="round" />
                  <circle cx="12" cy="16.5" r="0.6" fill="currentColor" stroke="none" />
                </svg>
              </div>
              <div className="state-title">Не удалось загрузить каталог</div>
              <p className="state-sub">Проверь подключение к сети и попробуй ещё раз.</p>
              <button className="btn-secondary" onClick={() => goToPage(page)}>Попробовать снова</button>
            </div>
          </div>
        )}

        {/* empty */}
        {!loading && !error && data && data.items.length === 0 && (
          <div className="catalog-grid">
            <div className="catalog-state">
              <div className="state-icon gold">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="8" r="3.5" />
                  <path d="M3 21a9 9 0 0 1 18 0" strokeLinecap="round" />
                  <path d="M12 14v7M9 17h6" strokeLinecap="round" />
                </svg>
              </div>
              <div className="state-title">Блогеров пока нет</div>
              <p className="state-sub">Стань первым — подключи Instagram и попади в каталог рекламодателей.</p>
              <button className="btn-primary" onClick={() => navigate("/")}>Войти через Instagram</button>
            </div>
          </div>
        )}

        {/* loaded */}
        {!loading && data && data.items.length > 0 && (
          <>
            <div className="catalog-grid">
              {data.items.map(card => (
                <CreatorCard
                  key={card.username}
                  card={card}
                  onClick={() => navigate(`/report/${card.username}`)}
                />
              ))}
            </div>

            {data.total_pages > 1 && (
              <nav className="catalog-pagination" aria-label="Страницы каталога">
                <button
                  className="catalog-pagination__btn"
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                  aria-label="Предыдущая"
                >
                  <ArrowLeftIcon /> Назад
                </button>

                {Array.from({ length: data.total_pages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    className={`catalog-pagination__btn${p === page ? " catalog-pagination__btn--active" : ""}`}
                    onClick={() => goToPage(p)}
                    aria-label={`Страница ${p}`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  className="catalog-pagination__btn"
                  disabled={page >= data.total_pages}
                  onClick={() => goToPage(page + 1)}
                  aria-label="Следующая"
                >
                  Вперёд <ArrowRightIcon />
                </button>
              </nav>
            )}
          </>
        )}
      </main>
    </div>
  );
};
